import {Group} from '@mantine/core';
import React from 'react';

import {ArchivableItem} from 'archive/ArchiveConstants';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import licenses from 'user/Licenses';
import {capwords, typeToName} from 'util/NameUtil';

import SchemaSettingsExportDropdown from './SchemaSettingsExportDropdown';

export interface ArchiveSchemaConfig {
  onArchive: (id: string, purpose: string) => void;
  onUnarchive: (id: string) => void;
  isArchivable: boolean;
  isUnarchivable: boolean;
}

interface SchemaSettingsPageContainerProps {
  archiveConfig?: ArchiveSchemaConfig;
  children: React.ReactNode;
  onExportSpreadsheetTemplate?: () => void;
  onNavigateToListing: () => void;
  // Include schema if editing, or null if creating.
  schema: ArchivableItem | null;
  schemaType: ModelTypeConstants;
}

interface SchemaSettingsPageContainerToolbarProps {
  archiveConfig?: ArchiveSchemaConfig;
  onExportSpreadsheetTemplate?: () => void;
  schema: ArchivableItem;
  schemaType: ModelTypeConstants;
}

function SchemaSettingsPageContainerToolbar(props: SchemaSettingsPageContainerToolbarProps): JSX.Element {
  return (
    <Group spacing="sm">
      {props.archiveConfig && (
        <ArchiveOrUnarchiveButton
          item={props.schema}
          isArchivable={props.archiveConfig.isArchivable}
          isUnarchivable={props.archiveConfig.isUnarchivable}
          onArchive={(_, purpose) => props.archiveConfig?.onArchive(props.schema.id, purpose)}
          onUnarchive={(_) => props.archiveConfig?.onUnarchive(props.schema.id)}
        />
      )}
      {licenses.hasEnterprisePlan() && (
        <SchemaSettingsExportDropdown
          onExportSpreadsheetTemplate={props.onExportSpreadsheetTemplate}
          schemaId={props.schema.id}
          schemaType={props.schemaType}
        />
      )}
    </Group>
  );
}

/*
 * Container component for a schema page, where a user is either creating or editing a schema.
 */
export default function SchemaSettingsPageContainer(props: SchemaSettingsPageContainerProps): JSX.Element {
  return (
    <SettingsPageContainer
      onClickTitle={props.onNavigateToListing}
      subtitle={
        props.schema ? (
          <span>
            {props.schema.name}
            <ArchiveRecordLabel item={props.schema} />
          </span>
        ) : (
          ''
        )
      }
      title={`${capwords(typeToName(props.schemaType))}s`}
      toolbar={
        props.schema && (
          <SchemaSettingsPageContainerToolbar
            archiveConfig={props.archiveConfig}
            onExportSpreadsheetTemplate={props.onExportSpreadsheetTemplate}
            schema={props.schema}
            schemaType={props.schemaType}
          />
        )
      }
    >
      {props.children}
    </SettingsPageContainer>
  );
}
